/**
 * Pretplata (A5, Luka's WEB5): the four plans as a radio group (3 months preselected, no
 * badge, R3), what every plan includes (R2), the CTA "Izaberi paket — 26,99 €" with the
 * renewal and cancellation text next to it (Q6), and the 5-day no-card trial (R4, R5).
 *
 * Site handoff (§6): `?plan=` preselects a plan (allowlist), `?lang=` is applied app-wide by
 * LanguageParamHandler. Signed-out visitors register first (the trial starts there) and come
 * back here with the plan; signed-in visitors go to Stripe Checkout; paying users manage
 * their one subscription in the customer portal.
 */
import { useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Sparkles, Heart, Loader2, Check } from 'lucide-react';
import { Alert, AlertDescription, Button, Card, CardContent } from '@/components/ui';
import { LegalLinks, PlanPicker, RenewalDisclosure } from '@/components/features';
import { useLanguage, useIsAuthenticated, useUser } from '@/store';
import { usePricingPlans, useCreatePortalSession, useCreateCheckout, useSubscriptionStatus } from '@/hooks';
import {
  getPricingTranslation,
  getPlanPickerTranslation,
  getBottomNavTranslation,
  fillDate,
  formatPrice,
  withRedirect,
  isTrialActive,
} from '@/utils';
import { sitePageUrl } from '@/utils/legal';
import { isSubscriptionExistsError } from '@/services/api';
import { DEFAULT_PLAN_ID, PLAN_IDS, isPlanId, parsePlanId, type PlanId } from '@/config/plans';
import { ROUTES } from '@/config/routes';
import { isAcousticEnabled } from '@/config/features';
import type { BottomNavTranslationKey } from '@/utils/translations';

/** R2: every plan and the trial include the same things (Akustične once A4 is live). */
function includedFeatures(): BottomNavTranslationKey[] {
  return isAcousticEnabled() ? ['guidedSessions', 'acousticSessions', 'journal'] : ['guidedSessions', 'journal'];
}

export function PricingPage() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: plansData, isLoading, error } = usePricingPlans();
  // Fresher than the user stored at sign-in: a lapsed plan must reach Checkout, not the portal.
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const createCheckout = useCreateCheckout();
  const createPortal = useCreatePortalSession();
  const headingId = useId();
  const disclosureId = useId();
  const includesId = useId();

  const t = (key: Parameters<typeof getPricingTranslation>[0]) => getPricingTranslation(key, language);
  const tp = (key: Parameters<typeof getPlanPickerTranslation>[0]) => getPlanPickerTranslation(key, language);

  const requestedPlan = parsePlanId(searchParams.get('plan'));
  const currentPlan = isPlanId(user?.subscription_plan) ? user.subscription_plan : null;
  const [selected, setSelected] = useState<PlanId>(requestedPlan ?? currentPlan ?? DEFAULT_PLAN_ID);

  // A new ?plan= (e.g. a second click on the site) selects that plan (state adjusted during render).
  const [lastRequested, setLastRequested] = useState(requestedPlan);
  if (requestedPlan !== lastRequested) {
    setLastRequested(requestedPlan);
    if (requestedPlan) setSelected(requestedPlan);
  }

  const isPremium = Boolean(subscriptionStatus?.is_premium ?? user?.is_premium);
  const trialEnd = user?.trial_expires ? new Date(user.trial_expires) : null;
  const trialActive = isTrialActive(user);

  const plans = plansData?.plans ?? {};
  const available = PLAN_IDS.filter((id) => plans[id]);
  const planId = available.includes(selected) ? selected : (available[0] ?? DEFAULT_PLAN_ID);
  const price = plans[planId]?.price;

  const handleChoose = () => {
    if (!isAuthenticated) {
      // The trial starts at registration; afterwards the visitor comes back to this plan.
      navigate(withRedirect(ROUTES.register, `${ROUTES.subscription}?plan=${planId}`));
      return;
    }
    if (isPremium) {
      // One subscription per customer: plan changes happen in the Stripe customer portal.
      createPortal.mutate();
      return;
    }
    createCheckout.mutate(
      { planId, language },
      {
        // Stripe still has a subscription for this customer (e.g. past_due while app
        // access has lapsed): manage it in the portal.
        onError: (checkoutError) => {
          if (isSubscriptionExistsError(checkoutError)) createPortal.mutate();
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="container py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || price === undefined) {
    return (
      <div className="container py-16">
        <Alert variant="destructive">
          <AlertDescription>{t('loadError')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const checkoutFailed = createCheckout.isError && !isSubscriptionExistsError(createCheckout.error);
  const isPending = createCheckout.isPending || createPortal.isPending;

  return (
    <div className="container py-4 sm:py-8 md:py-16">
      <div className="mx-auto max-w-xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 id={headingId} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
            {t('chooseYourJourney')}
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground">{t('unlockPotential')}</p>
        </div>

        {/* Where the signed-in user stands */}
        {isAuthenticated && user && (isPremium || trialEnd) && (
          <Alert className="mb-6">
            {isPremium ? <Shield className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            <AlertDescription>
              {isPremium ? (
                t('activeSubscription')
              ) : trialActive ? (
                <>
                  <span className="block">{fillDate(t('trialActiveUntil'), trialEnd!, language)}</span>
                  <span className="block">{tp('trialPayNow')}</span>
                </>
              ) : (
                fillDate(t('trialExpiredOn'), trialEnd!, language)
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* 5-day free trial: no card, starts at registration, one per account (R4) */}
        {!isAuthenticated && (
          <Card className="mb-6">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold">{tp('freeTrialPlan')}</h2>
              <p className="mt-1 text-sm">{tp('fullAccess5Days')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{tp('trialStartsAtRegistration')}</p>
              <Button asChild variant="outline" className="mt-4 min-h-11 w-full">
                <Link to={ROUTES.register}>{tp('startFreeTrial')}</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* The four plans */}
        <PlanPicker plans={plans} selected={planId} onSelect={setSelected} labelledBy={headingId} />

        {/* Same access in every plan (R2) */}
        <div className="mt-6">
          <p id={includesId} className="font-medium">
            {tp('allPlansInclude')}
          </p>
          <ul aria-labelledby={includesId} className="mt-2 space-y-2">
            {includedFeatures().map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                {getBottomNavTranslation(feature, language)}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA with the renewal and cancellation text next to it (Q6) */}
        <div className="mt-6 space-y-3">
          <Button
            type="button"
            size="lg"
            className="min-h-11 h-auto w-full whitespace-normal py-3"
            onClick={handleChoose}
            disabled={isPending}
            aria-describedby={disclosureId}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                {tp('processing')}
              </>
            ) : isPremium ? (
              t('manageSubscription')
            ) : (
              tp('choosePlanCta').replace('{price}', formatPrice(price))
            )}
          </Button>
          <RenewalDisclosure id={disclosureId} planId={planId} price={price} className="text-center" />
          {(checkoutFailed || createPortal.isError) && (
            <p role="alert" className="text-sm text-destructive text-center">
              {tp('somethingWentWrong')}
            </p>
          )}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mt-16 text-center">
        <h2 className="text-2xl font-bold mb-8">{t('whyChooseLukaaji')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Heart className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">{t('guidedSessions')}</h3>
            <p className="text-sm text-muted-foreground">{t('guidedSessionsDesc')}</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">{t('securePrivate')}</h3>
            <p className="text-sm text-muted-foreground">{t('securePrivateDesc')}</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">{t('regularUpdates')}</h3>
            <p className="text-sm text-muted-foreground">{t('regularUpdatesDesc')}</p>
          </div>
        </div>
      </div>

      {/* FAQ Link */}
      <div className="mt-12 text-center">
        <p className="text-muted-foreground">
          {t('haveQuestions')}{' '}
          <a href={sitePageUrl('faq', language)} className="text-primary hover:underline">
            {t('checkFaq')}
          </a>
        </p>
      </div>

      {/* Terms and Privacy (lukaaji.com) */}
      <LegalLinks className="mt-8" />
    </div>
  );
}
